// router/routes.ts - Route Configuration
//
// Page handlers are imported *lazily* via dynamic `import()` so each route
// becomes its own webpack chunk and is only downloaded when the user
// actually navigates to it. The handler factories below preserve the
// existing `RouteHandler` contract (`() => Promise<void> | void`).
//
// `webpackChunkName` magic comments produce stable, readable chunk
// filenames (e.g. `page-viewSharedEntry.[hash].chunk.js`) which makes the
// network panel and Sentry traces far easier to read than the default
// hashed names. Multiple routes pointing at the same chunk name are
// deduplicated automatically by webpack.

import { isAppDevEnvironment } from "../utils/appEnv";

/**
 * Type definition for a route handler function
 */
export type RouteHandler = () => Promise<void> | void;

/**
 * Interface for a route definition
 */
export interface RouteDefinition {
	path: string;
	handler: RouteHandler;
	title: string | ((params: Record<string, string>) => string);
	pattern?: RegExp; // For dynamic routes
	isDynamic?: boolean;
}

/** Dev-only routes (recording timer lab). Lazy-loaded so dev pages stay out of the prod bundle. */
const devPreviewRoutes: RouteDefinition[] = [
	{
		path: "/dev/record-your-shot",
		handler: () =>
			import(
				/* webpackChunkName: "page-recordingCountdownDev" */ "../pages/recordingCountdown.dev"
			).then((m) => m.renderRecordingCountdownDevPage()),
		title: "Record your shot (dev) | Ace Society",
	},
	{
		path: "/dev/recording-countdown",
		handler: () =>
			import(
				/* webpackChunkName: "page-recordingCountdownDev" */ "../pages/recordingCountdown.dev"
			).then((m) => m.renderRecordingCountdownDevPage()),
		title: "Record your shot (dev) | Ace Society",
	},
];

/**
 * All production routes, static and dynamic. Page handlers are lazy-loaded.
 */
const productionRoutes: RouteDefinition[] = [
	// Static Routes
	{
		path: "/",
		handler: () =>
			import(/* webpackChunkName: "page-home" */ "../pages/home").then((m) =>
				m.renderHomePage(),
			),
		title: "Home | Ace Society",
	},
	{
		path: "/for-courses",
		handler: () =>
			import(/* webpackChunkName: "page-forCourses" */ "../pages/forCourses").then(
				(m) => m.renderForCoursesPage(),
			),
		title: "Information For Courses | Ace Society",
	},
	{
		path: "/login",
		handler: () =>
			import(/* webpackChunkName: "page-auth" */ "../pages/authPage").then((m) =>
				m.renderLoginPage(),
			),
		title: "Login | Ace Society",
	},
	{
		path: "/dashboard",
		handler: () =>
			import(/* webpackChunkName: "page-dashboard" */ "../pages/dashboard").then(
				(m) => m.renderDashboardPage(),
			),
		title: "Dashboard | Ace Society",
	},
	{
		path: "/dashboard/entries",
		handler: () =>
			import(
				/* webpackChunkName: "page-viewEntries" */ "../pages/viewEntries"
			).then((m) => m.renderEntriesPage()),
		title: "Your Entries | Ace Society",
	},
	{
		path: "/dashboard/timeline",
		handler: () =>
			import(
				/* webpackChunkName: "page-viewTimeline" */ "../pages/viewTimeline"
			).then((m) => m.renderTimelinePage()),
		title: "Your Timeline | Ace Society",
	},
	{
		path: "/contact-us",
		handler: () =>
			import(/* webpackChunkName: "page-contactUs" */ "../pages/contactUs").then(
				(m) => m.renderContactUsPage(),
			),
		title: "Contact Us | Ace Society",
	},
	{
		path: "/reset-password",
		handler: () =>
			import(/* webpackChunkName: "page-auth" */ "../pages/authPage").then((m) =>
				m.renderResetPasswordPage(),
			),
		title: "Reset Password | Ace Society",
	},
	{
		path: "/leaderboard",
		handler: () =>
			import(
				/* webpackChunkName: "page-viewLeaderboard" */ "../pages/viewLeaderboard"
			).then((m) => m.renderLeaderboardPage()),
		title: "Leaderboard | Ace Society",
	},
	{
		path: "/hall-of-fame",
		handler: () =>
			import(/* webpackChunkName: "page-hallOfFame" */ "../pages/hallOfFame").then(
				(m) => m.renderHallOfFamePage(),
			),
		title: "Hall of Fame | Ace Society",
	},
	{
		path: "/inner-circle-wins",
		handler: () =>
			import(
				/* webpackChunkName: "page-innerCircleWins" */ "../pages/innerCircleWins"
			).then((m) => m.renderInnerCircleWinsPage()),
		title: "Inner Circle Wins | Ace Society",
	},
	{
		path: "/courses",
		handler: () =>
			import(
				/* webpackChunkName: "page-coursePartners" */ "../pages/coursePartners"
			).then((m) => m.renderCoursePartnersPage()),
		title: "Our Course Partners | Ace Society",
	},
	{
		path: "/leaderboard/prizes-and-points",
		handler: () =>
			import(
				/* webpackChunkName: "page-prizesAndPoints" */ "../pages/prizesAndPoints"
			).then((m) => m.renderPrizesAndPointsPage()),
		title: "Prizes and Points | Ace Society",
	},
	{
		path: "/terms",
		handler: () =>
			import(/* webpackChunkName: "page-terms" */ "../pages/terms").then((m) =>
				m.renderTermsPage(),
			),
		title: "Terms and Conditions | Ace Society",
	},
	{
		path: "/competition-terms",
		handler: () =>
			import(
				/* webpackChunkName: "page-competitionTerms" */ "../pages/competitionTerms"
			).then((m) => m.renderCompetitionTermsPage()),
		title: "Competition Terms and Conditions | Ace Society",
	},
	{
		path: "/privacy",
		handler: () =>
			import(/* webpackChunkName: "page-privacy" */ "../pages/privacy").then((m) =>
				m.renderPrivacyPage(),
			),
		title: "Privacy Policy | Ace Society",
	},
	{
		path: "/frequently-asked-questions",
		handler: () =>
			import(/* webpackChunkName: "page-faq" */ "../pages/faq").then((m) =>
				m.renderFAQPage(),
			),
		title: "Frequently Asked Questions | Ace Society",
	},
	{
		path: "/inner-circle-faq",
		handler: () =>
			import(
				/* webpackChunkName: "page-innerCircleFaq" */ "../pages/innerCircleFaq"
			).then((m) => m.renderInnerCircleFAQPage()),
		title: "Inner Circle FAQ | Ace Society",
	},
	{
		path: "/feedback-competition-terms",
		handler: () =>
			import(
				/* webpackChunkName: "page-feedbackTerms" */ "../pages/feedbackTerms"
			).then((m) => m.renderFeedbackTermsPage()),
		title: "Feedback Competition Terms and Conditions | Ace Society",
	},
	{
		path: "/survey/ace-society-championship",
		handler: () =>
			import(
				/* webpackChunkName: "page-surveyChampionship" */ "../pages/survey-championship"
			).then((m) => m.renderSurveyChampionshipPage()),
		title: "Ace Society Championship Survey | Ace Society",
	},

	// Dynamic Routes
	{
		path: "/courses/:slug",
		pattern: /^\/courses\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(
				/* webpackChunkName: "page-coursePartner" */ "../pages/coursePartner"
			).then((m) => m.renderCoursePartnerPage()),
		title: "Our Course Partners | Ace Society",
		isDynamic: true,
	},
	{
		path: "/enter-v2/:holeSlug",
		pattern: /^\/enter-v2\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(/* webpackChunkName: "page-enterHole" */ "../pages/enterHole").then(
				(m) => m.renderEnterHolePage(),
			),
		title: "Enter Hole | Ace Society",
		isDynamic: true,
	},
	{
		path: "/enter/:holeSlug",
		pattern: /^\/enter\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(/* webpackChunkName: "page-enterHole" */ "../pages/enterHole").then(
				(m) => m.renderEnterHolePage(),
			),
		title: "Enter Hole | Ace Society",
		isDynamic: true,
	},
	{
		path: "/web/enter/:holeSlug",
		pattern: /^\/web\/enter\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(/* webpackChunkName: "page-enterHole" */ "../pages/enterHole").then(
				(m) => m.renderEnterHolePage(),
			),
		title: "Enter Hole | Ace Society",
		isDynamic: true,
	},
	{
		path: "/dashboard/entry/:id",
		pattern: /^\/dashboard\/entry\/(\d+)$/,
		handler: () =>
			import(
				/* webpackChunkName: "page-viewRecording" */ "../pages/viewRecording"
			).then((m) => m.renderViewRecordingPage()),
		title: "View Entry | Ace Society",
		isDynamic: true,
	},
	{
		path: "/dashboard/group/:id",
		pattern: /^\/dashboard\/group\/(\d+)$/,
		handler: () =>
			import(
				/* webpackChunkName: "page-groupDetails" */ "../pages/groupDetails"
			).then((m) => m.renderGroupDetailsPage()),
		title: "Group Entries | Ace Society",
		isDynamic: true,
	},
	{
		path: "/s/:shareSlug",
		pattern: /^\/s\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(
				/* webpackChunkName: "page-viewSharedEntry" */ "../pages/viewSharedEntry"
			).then((m) => m.renderSharedEntryPage()),
		title: "View Entry | Ace Society",
		isDynamic: true,
	},
	{
		path: "/select-course/:courseName",
		pattern: /^\/select-course\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(
				/* webpackChunkName: "page-selectCourse" */ "../pages/selectCourse"
			).then((m) => m.renderSelectCoursePage()),
		title: (params) =>
			`${(params.courseName || "Course").replace(
				/-/g,
				" ",
			)} - Select Hole | Ace Society`,
		isDynamic: true,
	},
	{
		path: "/reset-password/:token",
		pattern: /^\/reset-password\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(/* webpackChunkName: "page-auth" */ "../pages/authPage").then((m) =>
				m.renderDoResetPasswordPage(),
			),
		title: "Reset Password | Ace Society",
		isDynamic: true,
	},
	{
		path: "/set-password-after-claim/:token",
		pattern: /^\/set-password-after-claim\/([a-zA-Z0-9_-]+)$/,
		handler: () =>
			import(/* webpackChunkName: "page-auth" */ "../pages/authPage").then((m) =>
				m.renderSetPasswordAfterClaimPage(),
			),
		title: "Set Your Password | Ace Society",
		isDynamic: true,
	},
	{
		path: "/leaderboard/:courseSlug",
		pattern: /^\/leaderboard\/([a-zA-Z0-9_-]+)$/,
		handler: async () => {
			const path = window.location.pathname;
			const slug = path.match(/^\/leaderboard\/([a-zA-Z0-9_-]+)$/);
			if (slug) {
				const m = await import(
					/* webpackChunkName: "page-courseLeaderboard" */ "../pages/courseLeaderboard"
				);
				await m.renderCourseLeaderboardPage(slug[1]);
			}
		},
		title: "Course Leaderboard | Ace Society",
		isDynamic: true,
	},
	{
		path: "/admin/inner-circle/pin-location/:id",
		pattern: /^\/admin\/inner-circle\/pin-location\/(\d+)$/,
		handler: async () => {
			const path = window.location.pathname;
			const match = path.match(/^\/admin\/inner-circle\/pin-location\/(\d+)$/);
			if (match) {
				const m = await import(
					/* webpackChunkName: "page-adminInnerCirclePinLocation" */ "../pages/adminInnerCirclePinLocation"
				);
				await m.renderAdminInnerCirclePinLocationPage(match[1]);
			}
		},
		title: "Inner Circle Pin Location | Ace Society",
		isDynamic: true,
	},
	{
		path: "/admin/inner-circle/hourly-check/:batchId",
		pattern: /^\/admin\/inner-circle\/hourly-check\/(\d+)$/,
		handler: async () => {
			const path = window.location.pathname;
			const match = path.match(/^\/admin\/inner-circle\/hourly-check\/(\d+)$/);
			if (match) {
				const m = await import(
					/* webpackChunkName: "page-adminHourlyPinCheck" */ "../pages/adminHourlyPinCheck"
				);
				await m.renderAdminHourlyPinCheckPage(match[1]);
			}
		},
		title: "Hourly Pin Check | Ace Society",
		isDynamic: true,
	},
	{
		path: "/admin/inner-circle/hourly-check/:batchId/items/:itemId",
		pattern: /^\/admin\/inner-circle\/hourly-check\/(\d+)\/items\/(\d+)$/,
		handler: async () => {
			const path = window.location.pathname;
			const match = path.match(
				/^\/admin\/inner-circle\/hourly-check\/(\d+)\/items\/(\d+)$/,
			);
			if (match) {
				const m = await import(
					/* webpackChunkName: "page-adminHourlyPinCheck" */ "../pages/adminHourlyPinCheck"
				);
				await m.renderAdminHourlyPinCheckPage(match[1], match[2]);
			}
		},
		title: "Hourly Pin Check | Ace Society",
		isDynamic: true,
	},
];

const dynamicRouteStartIndex = productionRoutes.findIndex(
	(r) => r.path === "/enter-v2/:holeSlug",
);

/** All client-side routes (dev preview routes omitted unless APP_ENV is dev/development). */
export const routes: RouteDefinition[] = [
	...productionRoutes.slice(0, dynamicRouteStartIndex),
	...(isAppDevEnvironment() ? devPreviewRoutes : []),
	...productionRoutes.slice(dynamicRouteStartIndex),
];

/**
 * Check if a route is handled client-side
 * @param path - The path to check
 * @returns true if the route should be handled client-side
 */
export function isClientSideRoute(path: string): boolean {
	return routes.some(
		(route) =>
			(!route.isDynamic && route.path === path) ||
			(route.isDynamic && route.pattern && route.pattern.test(path)),
	);
}

/**
 * Get the route definition for a given path
 * @param path - The path to match
 * @returns The route definition or null if not found
 */
export function getRoute(path: string): RouteDefinition | null {
	for (const route of routes) {
		if (!route.isDynamic && route.path === path) {
			return route;
		}
		if (route.isDynamic && route.pattern && route.pattern.test(path)) {
			return route;
		}
	}
	return null;
}

/**
 * Extract parameters from a dynamic route
 * @param path - The actual path
 * @param route - The route definition
 * @returns Object with extracted parameters
 */
export function extractRouteParams(
	path: string,
	route: RouteDefinition,
): Record<string, string> {
	const params: Record<string, string> = {};
	if (!route.pattern) return params;

	const match = path.match(route.pattern);
	if (!match) return params;

	const keys = route.path.match(/:(\w+)/g) || [];
	keys.forEach((key, index) => {
		params[key.substring(1)] = match[index + 1];
	});

	return params;
}
